// src/pages/BlogDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getPostBySlug, getRecentPosts, blogPosts } from '../data/blogData';
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import '../styles/BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const currentPost = getPostBySlug(slug);
    if (currentPost) {
      setPost(currentPost);

      // Get related posts (same category, exclude current)
      const related = blogPosts
        .filter(p => p.category === currentPost.category && p.id !== currentPost.id)
        .slice(0, 3);

      // If not enough related posts, fill with recent posts
      if (related.length < 3) {
        const recentPosts = getRecentPosts(5).filter(p => p.id !== currentPost.id);
        const combined = [...related, ...recentPosts];
        setRelatedPosts(combined.slice(0, 3));
      } else {
        setRelatedPosts(related);
      }

      // Scroll to top
      window.scrollTo(0, 0);
    } else {
      navigate('/blog');
    }
  }, [slug, navigate]);

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = post.title;

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
    } else if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    }
  };

  if (!post) {
    return (
      <div className="blog-detail-page">
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Find previous and next posts
  const currentIndex = blogPosts.findIndex(p => p.id === post.id);
  const previousPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <div className="blog-detail-page">
      <SEO
        title={`${post.title} | EDSY Tech`}
        description={post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        image={post.image}
        keywords={post.tags.join(', ')}
        type="article"
      />
      <Navbar />

      {/* Back Button */}
      <div className="back-button-container">
        <Link to="/blog" className="back-button">
          
        </Link>
      </div>

      {/* Article Header */}
      <article className="article-container">
        <header className="article-header">
          <div className="article-meta-top">
            <span className="article-category">{post.category}</span>
            <span className="article-date">
              <Calendar className="meta-icon" />
              {new Date(post.publishedDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            <span className="article-read-time">
              <Clock className="meta-icon" />
              {post.readTime}
            </span>
          </div>

          <h1 className="article-title">{post.title}</h1>
          <p className="article-excerpt">{post.excerpt}</p>

          <div className="article-author-section">
            {/* <div className="article-author">
              <img src={post.author.avatar} alt={post.author.name} className="article-author-avatar" />
              <div className="article-author-info">
                <div className="article-author-name">{post.author.name}</div>
                <div className="article-author-role">{post.author.role}</div>
              </div>
            </div> */}

            <div className="article-share">
              <span className="share-label">Share:</span>
              <button
                onClick={() => handleShare('twitter')}
                className="share-button"
                aria-label="Share on Twitter"
              >
                <Twitter className="share-icon" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="share-button"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="share-icon" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="share-button"
                aria-label="Share on Facebook"
              >
                <Facebook className="share-icon" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className={`share-button ${copied ? 'share-button-copied' : ''}`}
                aria-label="Copy link"
              >
                <Share2 className="share-icon" />
                {copied && <span className="copied-tooltip">Copied!</span>}
              </button>
            </div>
          </div>

          {post.featured && (
            <div className="featured-article-badge">
              ⭐ Featured Article
            </div>
          )}
        </header>

        {/* Featured Image */}
        <div className="article-featured-image" style={{ backgroundImage: `url(${post.image})` }}>
        </div>

        {/* Article Content */}
        <div className="article-content">
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>

        {/* Article Tags */}
        <div className="article-tags-section">
          <h3 className="tags-title">Tags</h3>
          <div className="article-tags">
            {post.tags.map(tag => (
              <Link
                key={tag}
                to={`/blog?tag=${encodeURIComponent(tag)}`}
                className="article-tag"
              >
                <Tag className="tag-icon-small" />
                {tag}
              </Link>
            ))}
          </div>
        </div>

        {/* Author Bio */}
        {/* <div className="author-bio-section">
          <img src={post.author.avatar} alt={post.author.name} className="author-bio-avatar" />
          <div className="author-bio-content">
            <h3 className="author-bio-name">{post.author.name}</h3>
            <p className="author-bio-role">{post.author.role}</p>
            <p className="author-bio-description">
              {post.author.name} is a technology leader with over 15 years of experience in enterprise 
              software development, architecture, and team leadership. Passionate about building 
              scalable systems and empowering teams.
            </p>
          </div>
        </div> */}

        {/* Previous/Next Navigation */}
        <div className="article-navigation">
          {previousPost && (
            <Link to={`/blog/${previousPost.slug}`} className="nav-link nav-link-prev">
              <div className="nav-link-label">
                <ArrowLeft className="nav-arrow" />
                Previous Article
              </div>
              <div className="nav-link-title">{previousPost.title}</div>
            </Link>
          )}
          {nextPost && (
            <Link to={`/blog/${nextPost.slug}`} className="nav-link nav-link-next">
              <div className="nav-link-label">
                Next Article
                <ArrowRight className="nav-arrow" />
              </div>
              <div className="nav-link-title">{nextPost.title}</div>
            </Link>
          )}
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="related-articles-section">
          <div className="related-articles-container">
            <h2 className="related-articles-title">Related Articles</h2>
            <div className="related-articles-grid">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="related-article-card"
                >
                  <div className="related-article-image" style={{ backgroundImage: `url(${relatedPost.image})` }}>
                    <span className="related-article-category">{relatedPost.category}</span>
                  </div>
                  <div className="related-article-content">
                    <h3 className="related-article-title">{relatedPost.title}</h3>
                    <p className="related-article-excerpt">{relatedPost.excerpt}</p>
                    {/* <div className="related-article-meta">
                      <span className="related-article-author">
                        <img src={relatedPost.author.avatar} alt={relatedPost.author.name} className="related-author-avatar" />
                        {relatedPost.author.name}
                      </span>
                      <span className="related-article-read-time">
                        <Clock className="meta-icon-tiny" />
                        {relatedPost.readTime}
                      </span>
                    </div> */}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="blog-cta-section">
        <div className="blog-cta-container">
          <h2 className="blog-cta-title">Ready to Transform Your Platform?</h2>
          <p className="blog-cta-text">
            Let's discuss how we can help you implement these strategies in your organization.
          </p>
          <Link to="/contact" className="blog-cta-button">
            Book a Strategy Call
            <ArrowRight className="cta-arrow" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetail;